import type { AttendancePunchResult } from '@erp/shared';
import { isAxiosError } from 'axios';
import { CheckCircle2, Delete, Fingerprint, LogOut, ScanLine, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card } from '../../../components/ui/index.js';
import { attendanceErrorCode, formatMinutes, getAttendanceSettings, kioskPunch } from '../../../lib/attendance.js';
import { useAuthStore } from '../../../lib/authStore.js';
import { playSound } from '../../../lib/sounds.js';

type Step =
  | { kind: 'code' }
  | { kind: 'pin'; code: string }
  | { kind: 'done'; result: AttendancePunchResult }
  | { kind: 'error'; message: string };

/** How long a result stays on screen before the kiosk is ready for the next person. */
const RESET_MS = 5_000;
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/** A clock ticking once a second. Formatted in the SHOP's zone below, not the tablet's. */
function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/**
 * The attendance kiosk: a tablet or PC by the door, signed in once with a login
 * that holds `hr.time.kiosk` and nothing else. People scan their badge (the
 * employee code as a barcode — a USB scanner types it and presses Enter) or
 * type the code, then the PIN on the pad. The server decides in or out.
 *
 * Every refusal comes back as a 4xx that is not 401, so a wrong PIN never signs
 * the kiosk itself out. The screen resets itself after each person.
 */
export function AttendanceKioskPage(): JSX.Element {
  const { t, i18n } = useTranslation('hr');
  const tenantName = useAuthStore((s) => s.tenant?.name ?? '');
  const now = useClock();
  const [step, setStep] = useState<Step>({ kind: 'code' });
  const [code, setCode] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [requirePin, setRequirePin] = useState(true);
  const [timeZone, setTimeZone] = useState<string | undefined>(undefined);
  const codeRef = useRef<HTMLInputElement>(null);
  const resetTimer = useRef<number | null>(null);

  useEffect(() => {
    void getAttendanceSettings()
      .then((s) => { setRequirePin(s.kioskRequirePin); setTimeZone(s.timezone); })
      .catch(() => undefined);
  }, []);

  // Keep the screen on while the kiosk is open (best effort; unsupported browsers just sleep).
  useEffect(() => {
    let lock: { release: () => Promise<void> } | null = null;
    const nav = navigator as Navigator & { wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> } };
    const acquire = (): void => {
      void nav.wakeLock?.request('screen').then((l) => { lock = l; }).catch(() => undefined);
    };
    acquire();
    const onVisible = (): void => { if (document.visibilityState === 'visible') acquire(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release().catch(() => undefined);
    };
  }, []);

  const reset = useCallback((): void => {
    if (resetTimer.current) window.clearTimeout(resetTimer.current);
    resetTimer.current = null;
    setStep({ kind: 'code' });
    setCode('');
    setPin('');
    window.setTimeout(() => codeRef.current?.focus(), 0);
  }, []);

  useEffect(() => () => { if (resetTimer.current) window.clearTimeout(resetTimer.current); }, []);

  const scheduleReset = (): void => {
    if (resetTimer.current) window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(reset, RESET_MS);
  };

  const submit = async (employeeCode: string, withPin?: string): Promise<void> => {
    if (!employeeCode.trim() || busy) return;
    setBusy(true);
    try {
      const result = await kioskPunch(employeeCode.trim(), withPin);
      playSound('success');
      setStep({ kind: 'done', result });
      scheduleReset();
    } catch (err) {
      const reason = isAxiosError(err) && err.response?.status === 429 ? 'kiosk_throttled' : attendanceErrorCode(err);
      if (reason === 'pin_required') {
        setStep({ kind: 'pin', code: employeeCode.trim() });
      } else if (reason === 'wrong_pin') {
        playSound('error');
        setPin('');
        setStep({ kind: 'error', message: t('attendance.kiosk.errors.wrong_pin') });
        scheduleReset();
      } else {
        playSound('error');
        setStep({
          kind: 'error',
          message: t(`attendance.kiosk.errors.${reason ?? 'generic'}`, {
            defaultValue: t(`attendance.errors.${reason ?? 'generic'}`, { defaultValue: t('attendance.errors.generic') }),
          }),
        });
        scheduleReset();
      }
    } finally {
      setBusy(false);
    }
  };

  const onCode = (e: React.FormEvent): void => {
    e.preventDefault();
    const c = code.trim();
    if (!c) return;
    if (requirePin) setStep({ kind: 'pin', code: c });
    else void submit(c);
  };

  const press = (k: string): void => {
    if (step.kind !== 'pin' || busy) return;
    if (k === 'back') { setPin((p) => p.slice(0, -1)); return; }
    if (k === 'ok') { if (pin.length >= 4) void submit(step.code, pin); return; }
    setPin((p) => (p.length < 6 ? p + k : p));
  };

  // A physical keyboard works on the PIN step too.
  useEffect(() => {
    if (step.kind !== 'pin') return;
    const onKey = (e: KeyboardEvent): void => {
      if (/^\d$/.test(e.key)) press(e.key);
      else if (e.key === 'Backspace') press('back');
      else if (e.key === 'Enter') press('ok');
      else if (e.key === 'Escape') reset();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // The shop's clock, whatever zone the tablet happens to be set to — the same
  // clock the server records the punch on.
  const fmt = (opts: Intl.DateTimeFormatOptions): string => {
    try { return new Intl.DateTimeFormat(i18n.language, { ...opts, timeZone }).format(now); } catch { return ''; }
  };
  const time = fmt({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const date = fmt({ weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="mx-auto flex min-h-[calc(100dvh-8rem)] w-full max-w-xl flex-col items-center justify-center gap-6 py-6">
      <div className="text-center">
        <div className="text-sm font-medium text-fg-muted">{tenantName}</div>
        <div className="font-mono text-6xl font-bold tabular-nums tracking-tight" dir="ltr">{time}</div>
        <div className="mt-1 text-sm text-fg-muted">{date}</div>
      </div>

      <Card className="w-full p-6">
        {step.kind === 'code' && (
          <form onSubmit={onCode} className="flex flex-col items-center gap-4">
            <span className="grid h-16 w-16 place-items-center rounded-2xl bg-brand-subtle text-brand">
              <ScanLine className="h-8 w-8" aria-hidden />
            </span>
            <h1 className="text-center text-xl font-semibold">{t('attendance.kiosk.prompt')}</h1>
            <input
              ref={codeRef}
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              // A kiosk has no other field: keep the cursor here so the next scan lands.
              onBlur={() => window.setTimeout(() => codeRef.current?.focus(), 200)}
              placeholder={t('attendance.kiosk.codePlaceholder')}
              aria-label={t('attendance.kiosk.codePlaceholder')}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              className="h-14 w-full rounded-xl border border-border bg-bg px-4 text-center font-mono text-2xl uppercase tracking-widest text-fg outline-none placeholder:font-sans placeholder:text-lg placeholder:normal-case placeholder:tracking-normal focus:border-brand"
              dir="ltr"
            />
            <Button type="submit" size="lg" className="w-full" loading={busy} disabled={!code.trim()}>
              {t('attendance.kiosk.continue')}
            </Button>
          </form>
        )}

        {step.kind === 'pin' && (
          <div className="flex flex-col items-center gap-4">
            <div className="text-center">
              <div className="font-mono text-sm text-fg-muted" dir="ltr">{step.code.toUpperCase()}</div>
              <h1 className="text-xl font-semibold">{t('attendance.kiosk.pinPrompt')}</h1>
            </div>
            <div className="flex gap-2" dir="ltr" aria-live="polite" aria-label={t('attendance.kiosk.pinPrompt')}>
              {Array.from({ length: Math.max(4, pin.length) }, (_, i) => (
                <span key={i} className={`h-4 w-4 rounded-full border-2 ${i < pin.length ? 'border-brand bg-brand' : 'border-border'}`} />
              ))}
            </div>
            <div className="grid w-full max-w-xs grid-cols-3 gap-3" dir="ltr">
              {KEYS.map((k) => (
                <button key={k} type="button" onClick={() => press(k)} className="h-16 rounded-xl border border-border bg-bg-subtle font-mono text-2xl font-semibold hover:bg-bg-muted active:scale-95">
                  {k}
                </button>
              ))}
              <button type="button" onClick={() => press('back')} aria-label={t('attendance.kiosk.backspace')} className="grid h-16 place-items-center rounded-xl border border-border bg-bg-subtle hover:bg-bg-muted active:scale-95">
                <Delete className="h-6 w-6" aria-hidden />
              </button>
              <button type="button" onClick={() => press('0')} className="h-16 rounded-xl border border-border bg-bg-subtle font-mono text-2xl font-semibold hover:bg-bg-muted active:scale-95">
                0
              </button>
              <button
                type="button"
                onClick={() => press('ok')}
                disabled={pin.length < 4 || busy}
                className="h-16 rounded-xl bg-brand text-lg font-semibold text-brand-contrast disabled:opacity-50 active:scale-95"
              >
                {t('attendance.kiosk.ok')}
              </button>
            </div>
            <Button variant="ghost" onClick={reset}>{t('attendance.sessions.cancel')}</Button>
          </div>
        )}

        {step.kind === 'done' && (
          <button type="button" onClick={reset} className="flex w-full flex-col items-center gap-3 text-center">
            {step.result.action === 'in'
              ? <CheckCircle2 className="h-20 w-20 text-success" aria-hidden />
              : <LogOut className="h-20 w-20 text-brand rtl:rotate-180" aria-hidden />}
            <div className="text-2xl font-bold">
              {t(step.result.action === 'in' ? 'attendance.kiosk.welcome' : 'attendance.kiosk.goodbye', { name: step.result.employee.displayName })}
            </div>
            <div className="text-lg">
              {t(step.result.action === 'in' ? 'attendance.kiosk.inAt' : 'attendance.kiosk.outAt', {
                time: (step.result.action === 'in' ? step.result.record.checkIn : step.result.record.checkOut ?? '').slice(11),
              })}
            </div>
            {step.result.action === 'in' && step.result.day.lateMinutes > 0 && (
              <div className="rounded-full bg-bg-subtle px-3 py-1 text-sm font-medium text-warning">
                {t('attendance.summary.late')} <span className="font-mono tabular-nums">{formatMinutes(step.result.day.lateMinutes)}</span>
              </div>
            )}
            {step.result.action === 'out' && (
              <div className="text-sm text-fg-muted">
                {t('attendance.clock.todayWorked')} <span className="font-mono tabular-nums text-fg">{formatMinutes(step.result.day.workedMinutes)}</span>
              </div>
            )}
          </button>
        )}

        {step.kind === 'error' && (
          <button type="button" onClick={reset} className="flex w-full flex-col items-center gap-3 text-center">
            <XCircle className="h-20 w-20 text-danger" aria-hidden />
            <div className="text-xl font-semibold">{step.message}</div>
            <div className="text-sm text-fg-muted">{t('attendance.kiosk.tapToRetry')}</div>
          </button>
        )}
      </Card>

      <p className="flex items-center gap-2 text-xs text-fg-subtle">
        <Fingerprint className="h-4 w-4" aria-hidden />
        {t('attendance.kiosk.footer')}
      </p>
    </div>
  );
}
